import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Check, Menu, MessageCircle, MessageSquarePlus, MoreVertical, Search, ShieldOff, UserPlus, Users, Volume2, X, type LucideIcon } from 'lucide-react';
import type { Relationship } from '@spawnwire/shared';
import { Avatar, Badge, Button, clsx, ContextMenu, DropdownMenu, EmptyState, IconButton, Input, statusLabel, Tooltip } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { attempt, openDm } from '../../lib/data';
import { useIsMobile } from '../../lib/hooks';
import { NameText } from '../../lib/nameFonts';
import { channelPath } from '../../lib/nav';
import { privateChannelName, useStore } from '../../store/store';
import { ui } from '../../store/ui';
import { userMenu } from '../members/userMenu';

type Tab = 'online' | 'all' | 'pending' | 'blocked' | 'add';

function AddFriend() {
  const [username, setUsername] = useState('');
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!username.trim()) return;
    setBusy(true);
    try {
      const rel = await http.post<Relationship>('/relationships', { username: username.trim().replace(/^@/, '').toLowerCase() });
      setResult({ ok: true, text: rel.type === 'friend' ? `You and ${rel.user.displayName} are now friends.` : `Friend request sent to ${rel.user.displayName}.` });
      setUsername('');
    } catch (err) {
      setResult({ ok: false, text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="border-b border-line px-6 py-5">
      <h2 className="text-base font-semibold text-fg">Add friend</h2>
      <p className="mt-1 text-sm text-muted">You can add friends with their Spawnwire username.</p>
      <form onSubmit={submit} className="mt-4 flex items-center gap-2 rounded-lg border border-line bg-input p-1.5 pl-3 focus-within:border-accent">
        <input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="You can add friends with their username."
          aria-label="Username"
          autoFocus
          className="min-w-0 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-faint"
        />
        <Button type="submit" size="sm" loading={busy} disabled={!username.trim()}>
          Send friend request
        </Button>
      </form>
      {result && <p className={clsx('mt-2 text-sm', result.ok ? 'text-success' : 'text-danger')}>{result.text}</p>}
    </div>
  );
}

/** Round grey action button used on friend rows. */
function RowAction({ icon: Icon, label, onClick, tone }: { icon: LucideIcon; label: string; onClick?: () => void; tone?: 'success' | 'danger' }) {
  return (
    <Tooltip label={label}>
      <button
        type="button"
        aria-label={label}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        className={clsx(
          'flex size-9 items-center justify-center rounded-full bg-sidebar text-muted transition-colors',
          tone === 'success' ? 'hover:text-success' : tone === 'danger' ? 'hover:text-danger' : 'hover:text-fg',
        )}
      >
        <Icon className="size-[18px]" aria-hidden />
      </button>
    </Tooltip>
  );
}

/** Friends who are in voice right now, grouped by the channel they share. */
function ActiveNow() {
  const navigate = useNavigate();
  const relationships = useStore((s) => s.relationships);
  const voiceStates = useStore((s) => s.voiceStates);
  const channels = useStore((s) => s.channels);
  const spaces = useStore((s) => s.spaces);
  const users = useStore((s) => s.users);

  const groups = useMemo(() => {
    const friends = new Set(Object.values(relationships).filter((r) => r.type === 'friend').map((r) => r.userId));
    return Object.entries(voiceStates)
      .map(([channelId, members]) => ({ channel: channels[channelId], ids: Object.keys(members).filter((id) => friends.has(id)) }))
      .filter((g) => g.channel && g.ids.length > 0);
  }, [relationships, voiceStates, channels]);

  return (
    <aside aria-label="Active now" className="scroll-thin hidden w-[360px] shrink-0 overflow-y-auto border-l border-line p-4 xl:block">
      <h2 className="mb-4 text-xl font-bold text-fg">Active Now</h2>
      {groups.length === 0 ? (
        <div className="px-2 py-4 text-center">
          <p className="font-semibold text-fg">It's quiet for now…</p>
          <p className="mt-1 text-sm text-muted">When a friend joins a voice channel or call, they'll show up here.</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {groups.map(({ channel, ids }) => {
            const ch = channel!;
            const people = ids.map((id) => users[id]).filter(Boolean);
            const place = ch.spaceId ? `${ch.name} · ${spaces[ch.spaceId]?.name ?? ''}` : privateChannelName(useStore.getState(), ch);
            return (
              <li key={ch.id}>
                <button
                  type="button"
                  onClick={() => navigate(channelPath(ch))}
                  className="w-full rounded-lg border border-line bg-sidebar p-3 text-left transition-colors hover:bg-elevated"
                >
                  <span className="flex items-center gap-3">
                    <span className="flex -space-x-2">
                      {people.slice(0, 3).map((u) => (
                        <Avatar key={u!.id} user={u} size={32} className="rounded-full ring-2 ring-sidebar" />
                      ))}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold text-fg">{people.map((u) => u!.displayName).join(', ')}</span>
                      <span className="block truncate text-xs text-muted">In a voice channel</span>
                    </span>
                  </span>
                  <span className="mt-3 flex items-center gap-2 rounded-md bg-main px-2.5 py-2 text-sm text-muted">
                    <Volume2 className="size-4 shrink-0 text-success" aria-hidden />
                    <span className="truncate">{place}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
}

export function FriendsView() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const relationships = useStore((s) => s.relationships);
  const presences = useStore((s) => s.presences);
  const users = useStore((s) => s.users);
  const voiceStates = useStore((s) => s.voiceStates);
  const location = useLocation();
  const [tab, setTab] = useState<Tab>('online');
  const [filter, setFilter] = useState('');

  // "Add Friends" on the phone layout opens this page on the Add Friend tab.
  const wantedTab = (location.state as { friendsTab?: Tab } | null)?.friendsTab;
  useEffect(() => {
    if (!wantedTab) return;
    setTab(wantedTab);
    // Only once: a reload or going back later opens the usual tab.
    navigate(location.pathname, { replace: true, state: null });
  }, [wantedTab, location.key, location.pathname, navigate]);

  const inVoice = useMemo(() => new Set(Object.values(voiceStates).flatMap((m) => Object.keys(m))), [voiceStates]);

  const list = useMemo(() => {
    const all = Object.values(relationships).map((r) => ({ ...r, user: users[r.userId] ?? r.user }));
    const byTab = all.filter((r) => {
      if (tab === 'online') return r.type === 'friend' && (presences[r.userId] ?? 'offline') !== 'offline';
      if (tab === 'all') return r.type === 'friend';
      if (tab === 'pending') return r.type === 'incoming' || r.type === 'outgoing';
      if (tab === 'blocked') return r.type === 'blocked';
      return false;
    });
    const q = filter.toLowerCase();
    return byTab.filter((r) => !q || r.user.displayName.toLowerCase().includes(q) || r.user.username.includes(q)).sort((a, b) => a.user.displayName.localeCompare(b.user.displayName));
  }, [relationships, presences, users, tab, filter]);

  const pendingCount = Object.values(relationships).filter((r) => r.type === 'incoming').length;
  const message = (userId: string) =>
    void openDm(userId).then((c) => {
      if (c) navigate(`/app/@me/${c.id}`);
    });

  const tabs: { id: Tab; label: string }[] = [
    { id: 'online', label: 'Online' },
    { id: 'all', label: 'All' },
    { id: 'pending', label: 'Pending' },
    { id: 'blocked', label: 'Blocked' },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex h-12 shrink-0 items-center gap-3 border-b border-line px-3">
        {isMobile && <IconButton icon={Menu} label="Open navigation" onClick={() => ui.setMobilePane('nav')} />}
        <span className="flex shrink-0 items-center gap-2 font-semibold text-fg">
          <Users className="size-5 text-muted" aria-hidden /> Friends
        </span>
        <span className="size-1 shrink-0 rounded-full bg-faint" aria-hidden />
        <nav className="flex min-w-0 items-center gap-2 overflow-x-auto [scrollbar-width:none]" role="tablist" aria-label="Friends">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={clsx('flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1 text-[15px] font-medium transition-colors', tab === t.id ? 'bg-active text-fg' : 'text-muted hover:bg-hover hover:text-fg')}
            >
              {t.label}
              {t.id === 'pending' && <Badge count={pendingCount} />}
            </button>
          ))}
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'add'}
            onClick={() => setTab('add')}
            className={clsx('shrink-0 rounded-md px-3 py-1 text-[15px] font-medium transition-colors', tab === 'add' ? 'bg-accent-soft text-accent' : 'bg-accent text-accent-fg hover:bg-accent-hover')}
          >
            Add Friend
          </button>
        </nav>
        <span className="ml-auto" />
        <IconButton icon={MessageSquarePlus} label="New group conversation" tooltipSide="bottom" onClick={() => ui.open({ kind: 'newGroup' })} />
      </header>

      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col">
          {tab === 'add' && <AddFriend />}
          {tab === 'add' ? (
            <div className="flex flex-1 items-center justify-center p-8 text-center text-sm text-faint">Friends you add show up under All once they accept.</div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col px-4 pt-4 sm:px-6">
              <div className="relative mb-4">
                <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search" aria-label="Filter friends" className="pl-9" />
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" aria-hidden />
              </div>
              <h2 className="mb-2 text-sm font-medium text-muted">
                {tabs.find((t) => t.id === tab)?.label} {'—'} {list.length}
              </h2>
              {list.length === 0 ? (
                <EmptyState
                  icon={tab === 'blocked' ? ShieldOff : Users}
                  title={tab === 'online' ? 'No one is around right now' : tab === 'pending' ? 'No pending requests' : tab === 'blocked' ? 'You have not blocked anyone' : 'No friends yet'}
                  body={tab === 'all' || tab === 'online' ? 'Add friends by username to start conversations and calls.' : undefined}
                  action={tab !== 'blocked' && tab !== 'pending' ? <Button icon={UserPlus} onClick={() => setTab('add')}>Add Friend</Button> : undefined}
                />
              ) : (
                <ul className="scroll-thin -mx-2 min-h-0 flex-1 overflow-y-auto pb-4">
                  {list.map((r) => {
                    const status = presences[r.userId] ?? 'offline';
                    const subtitle =
                      r.type === 'incoming'
                        ? 'Incoming friend request'
                        : r.type === 'outgoing'
                          ? 'Outgoing friend request'
                          : r.type === 'blocked'
                            ? 'Blocked'
                            : inVoice.has(r.userId)
                              ? 'In voice'
                              : statusLabel[status];
                    return (
                      <li key={r.userId} className="group/row border-t border-line first:border-t-0 hover:border-transparent [&:hover+li]:border-transparent">
                        <ContextMenu entries={userMenu(r.userId, null)}>
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={(e) => ui.openProfile(r.userId, null, e.currentTarget.querySelector('[data-avatar]') ?? e.currentTarget)}
                            onKeyDown={(e) => e.key === 'Enter' && ui.openProfile(r.userId, null, e.currentTarget)}
                            className="flex h-[62px] cursor-pointer items-center gap-3 rounded-lg px-2 hover:bg-hover"
                          >
                            <span data-avatar className="shrink-0">
                              <Avatar user={r.user} size={32} status={r.type === 'friend' ? status : null} ringClass="ring-main" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="flex items-baseline gap-1.5">
                                <span className="truncate font-semibold text-fg">
                                  <NameText font={r.user.nameFont}>{r.user.displayName}</NameText>
                                </span>
                                <span className="hidden truncate text-sm text-muted group-hover/row:inline">{r.user.username}</span>
                              </span>
                              <span className={clsx('flex items-center gap-1 truncate text-sm', subtitle === 'In voice' ? 'text-success' : 'text-muted')}>
                                {subtitle === 'In voice' && <Volume2 className="size-3.5" aria-hidden />}
                                {subtitle}
                              </span>
                            </span>
                            <div className="flex shrink-0 items-center gap-2">
                              {r.type === 'friend' && <RowAction icon={MessageCircle} label="Message" onClick={() => message(r.userId)} />}
                              {r.type === 'incoming' && <RowAction icon={Check} label="Accept" tone="success" onClick={() => void attempt(() => http.put(`/relationships/${r.userId}`, { type: 'friend' }))} />}
                              {(r.type === 'incoming' || r.type === 'outgoing') && (
                                <RowAction icon={X} label={r.type === 'incoming' ? 'Ignore' : 'Cancel request'} tone="danger" onClick={() => void attempt(() => http.del(`/relationships/${r.userId}`))} />
                              )}
                              {r.type === 'blocked' && (
                                <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); void attempt(() => http.del(`/relationships/${r.userId}`)); }}>
                                  Unblock
                                </Button>
                              )}
                              {r.type === 'friend' && (
                                <DropdownMenu
                                  entries={userMenu(r.userId, null)}
                                  trigger={
                                    <button
                                      type="button"
                                      aria-label="More"
                                      onClick={(e) => e.stopPropagation()}
                                      className="flex size-9 items-center justify-center rounded-full bg-sidebar text-muted transition-colors hover:text-fg"
                                    >
                                      <MoreVertical className="size-[18px]" aria-hidden />
                                    </button>
                                  }
                                />
                              )}
                            </div>
                          </div>
                        </ContextMenu>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
        <ActiveNow />
      </div>
    </div>
  );
}
